import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ProductItem, ColorVariant } from './types';
import { sanitizeUrl, getFallbackImageUrl, getColorSwatchFallback, getProxiedImageUrl } from './utils';
import { useImagePreloader } from '../../hooks/useImagePreloader';

interface ProductCardProps {
  product: ProductItem;
  scale?: number; // Scale factor 0.7 - 1.0, default 1.0
  activeColorFilter?: string | null;  // Color filter from facet toolbar - auto-selects matching variant
}

const ProductCardComponent: React.FC<ProductCardProps> = ({ product, scale = 1.0, activeColorFilter }) => {
  // Derive a preferred variant index using requested color context or backend-provided default
  const computePreferredIndex = (): number => {
    const variants = product.color_variants || [];
    if (!variants.length) return 0;

    // Backend hint (e.g., matched partialSku)
    const backendDefault = (product as any).default_variant_index;
    if (typeof backendDefault === 'number' && backendDefault >= 0 && backendDefault < variants.length) {
      // Even with backend default, prefer one with an image if the default lacks one
      const defaultVariant = variants[backendDefault];
      if (defaultVariant.image_url) {
        return backendDefault;
      }
      // Backend default has no image - look for one that does
      const withImage = variants.findIndex(v => v.image_url);
      return withImage >= 0 ? withImage : backendDefault;
    }

    const normalize = (v?: string | null) => (v ? v.toString().toLowerCase().replace(/[^a-z0-9]/g, '') : '');
    const requestedPool: string[] = [];

    // PRIORITY: Active color filter from facet toolbar (highest priority)
    if (activeColorFilter) {
      requestedPool.push(normalize(activeColorFilter));
    }

    // Possible color hints attached to the product
    const maybeColors = [
      (product as any).requested_color,
      (product as any).requested_color_code,
      (product as any).color_filter,
      product.attributes?.color_keywords,
      ...(Array.isArray((product as any).requested_colors) ? (product as any).requested_colors : []),
      ...(Array.isArray((product as any).query_color_codes) ? (product as any).query_color_codes : []),
      ...(Array.isArray((product as any).requested_color_names) ? (product as any).requested_color_names : []),
    ].filter(Boolean) as string[];

    maybeColors.forEach(c => {
      const norm = normalize(c);
      if (norm) requestedPool.push(norm);
    });

    // If we have color preferences, find matching variant
    if (requestedPool.length > 0) {
      // First try to find a matching variant that also has an image
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        const colorCandidates = [v.color_name, v.color_code, (v as any).variant_color_code];
        const variantNorms = colorCandidates.map(normalize).filter(Boolean);
        if (variantNorms.some(c => requestedPool.includes(c)) && v.image_url) {
          return i;
        }
      }
      // Fallback: matching variant without image
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        const colorCandidates = [v.color_name, v.color_code, (v as any).variant_color_code];
        const variantNorms = colorCandidates.map(normalize).filter(Boolean);
        if (variantNorms.some(c => requestedPool.includes(c))) {
          return i;
        }
      }
    }

    // No color preference - prefer first variant with a valid image
    const firstWithImage = variants.findIndex(v => v.image_url);
    if (firstWithImage >= 0) {
      return firstWithImage;
    }

    // Last resort: first variant
    return 0;
  };

  const defaultIndex = computePreferredIndex();
  const [selected, setSelected] = useState<ColorVariant | null>(
    product.color_variants && product.color_variants.length > 0 ? product.color_variants[defaultIndex] : null
  );
  const [isFlipped, setIsFlipped] = useState(false);
  const [isReasonsExpanded, setIsReasonsExpanded] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  // Auto-select color variant when activeColorFilter changes (facet filter selected)
  useEffect(() => {
    if (!activeColorFilter || !product.color_variants?.length) return;
    
    const normalize = (v?: string | null) => (v ? v.toString().toLowerCase().replace(/[^a-z0-9]/g, '') : '');
    const targetColor = normalize(activeColorFilter);
    
    // Find matching variant
    for (const variant of product.color_variants) {
      const colorCandidates = [variant.color_name, variant.color_code, (variant as any).variant_color_code];
      const variantNorms = colorCandidates.map(normalize).filter(Boolean);
      if (variantNorms.some(c => c.includes(targetColor) || targetColor.includes(c))) {
        setSelected(variant);
        return;
      }
    }
  }, [activeColorFilter, product.color_variants]);

  // Initialize image preloader
  const {
    preloadImage,
    isImageLoaded,
    isImageLoading,
    getImageError,
    cacheVersion
  } = useImagePreloader({ preloadPriority: 'high' });

  const cardRef = useRef<HTMLDivElement>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);

  // Get image URL and ensure it's properly encoded
  const rawImageUrl = selected?.image_url || product.primary_image || undefined;
  
  // First sanitize the URL to ensure proper encoding of SAS tokens
  const sanitizedUrl = sanitizeUrl(rawImageUrl);
  
  // Then proxy the URL to avoid CORS issues
  const imageUrl = getProxiedImageUrl(sanitizedUrl);
  
  // Get fallback image based on product category
  const fallbackImageUrl = getFallbackImageUrl(product.attributes?.category);
  
  const sku = selected?.sku || product.partialSku_OPIM || product.base_sku;

  // Start loading assets only when the card is near the viewport
  useEffect(() => {
    if (hasEnteredView) return;
    const node = cardRef.current;

    if (!node || typeof IntersectionObserver === 'undefined') {
      setHasEnteredView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasEnteredView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px 0px 200px 0px', threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasEnteredView]);

  // Check image status using preloader
  // IMPORTANT: Use the proxied imageUrl (what's actually rendered), not rawImageUrl!
  // This ensures the cache key matches what the browser is actually loading.
  const imageLoaded = useMemo(() => {
    const loaded = imageUrl ? isImageLoaded(imageUrl) : false;
    return loaded;
  }, [imageUrl, cacheVersion]);
  
  const imageIsLoading = useMemo(
    () => imageUrl ? isImageLoading(imageUrl) : false,
    [imageUrl, cacheVersion]
  );
  const imageError = useMemo(
    () => imageUrl ? getImageError(imageUrl) : false,
    [imageUrl, cacheVersion]
  );

  // Preload swatch images only after the card is on screen
  // IMPORTANT: Preload the proxied URLs (what's actually rendered)
  useEffect(() => {
    if (!hasEnteredView) return;
    if (product.color_variants && product.color_variants.length > 0) {
      product.color_variants.forEach((v: any) => {
        if (v?.swatch_url) {
          const proxiedSwatchUrl = getProxiedImageUrl(sanitizeUrl(v.swatch_url));
          if (proxiedSwatchUrl) preloadImage(proxiedSwatchUrl);
        }
      });
    }
  }, [hasEnteredView, product.color_variants, preloadImage]);

  // Always preload the current image (handles single-color products and variant changes)
  // IMPORTANT: Preload the proxied imageUrl (what's actually rendered), not rawImageUrl!
  useEffect(() => {
    if (hasEnteredView && imageUrl) {
      preloadImage(imageUrl);
    }
  }, [hasEnteredView, imageUrl, preloadImage]);

  // Handle color variant selection
  const handleVariantSelect = (variant: ColorVariant) => {
    setSelected(variant);
    // Image will already be preloaded, so switching will be instant
  };

  // Extract badges and reasons from product data
  // Check both top-level (new presenter) and attributes (legacy) locations
  const rawBadges = product.badges || product.attributes?.badges || [];
  // Remove leading emoji(s) from "Top 10 ..." badges (keep the text)
  const badges = rawBadges.map((b: string) => {
    if (!b || typeof b !== 'string') return b;
    if (!b.includes('Top 10')) return b;
    // Strip leading non-alphanumeric chars (emoji/punct) and whitespace
    return b.replace(/^[^a-zA-Z0-9]+/, '').trimStart();
  });
  const reasons = product.reasons || [];
  const stockItems = product.attributes?.stock_items || 0;
  const madeByUniFirst = (product.attributes as any)?.made_by_unifirst === true || (product.attributes as any)?.made_by_unifirst === 'Yes';
  const canadaOnly = (product.attributes as any)?.canada_only === true || (product.attributes as any)?.canada_only === 'Yes';
  const dsValue = (product.attributes as any)?.Direct_Sale_Only_or_Both;
  const directSaleOnly = dsValue === 'Direct Sale' || dsValue === 'Direct Sale Only';
  const rentalAndDirectSale = dsValue === 'Both Direct Sale & Rental' || dsValue === 'Both';
  const salesChannelUnknown = !dsValue;
  const hasCatalogSku = !!(product.attributes as any)?.catalog_sku;
  const isCatalogRental = hasCatalogSku && rentalAndDirectSale;

  // Collection info for variant grouping - use PIPS not glow
  const isCollection = product.is_collection === true;
  const collectionGlowColor = product.collection_glow_color || '#3b82f6';
  
  // NO glow style - we use pips instead for cleaner UI
  const collectionGlowStyle = {};
  
  // Semantic alternative indicator - products from pure semantic search (no filter matches)
  const isSemanticAlternative = product.is_semantic_alternative || (product as any).is_semantic_alternative;
  
  // Curated related product indicator - purple badge for catalog-related products
  const isCuratedRelated = product.is_curated_related || (product as any).is_curated_related;
  
  // Debug logging for curated flag
  if (isCuratedRelated) {
    console.log(`🏷️ CURATED BADGE ACTIVE for ${product.attributes?.style_code || sku}`);
  }

  // Calculate scaled values - more aggressive scaling for small sizes
  const scaledPadding = Math.max(6, Math.round(16 * scale));
  const scaledTitleSize = Math.max(11, Math.round(16 * scale));
  const scaledSmallText = Math.max(9, Math.round(12 * scale));
  const scaledTinyText = Math.max(8, Math.round(11 * scale));
  const scaledMargin = Math.max(3, Math.round(8 * scale));
  const scaledImageHeight = Math.max(80, Math.round(260 * scale));
  const scaledCardHeight = Math.max(220, Math.round(550 * scale));
  const scaledFlipHeight = Math.max(260, Math.round(645 * scale));

  return (
    <div 
      className="relative w-full cursor-pointer group"
      style={{ 
        perspective: '1000px',
        minHeight: `${scaledCardHeight}px`
      }}
      onClick={() => setIsFlipped(!isFlipped)}
      ref={cardRef}
    >
      <div 
        className="relative w-full transition-transform duration-500 ease-in-out"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          minHeight: `${scaledFlipHeight}px`
        }}
      >
        {/* FRONT SIDE */}
        <div 
          className={`absolute w-full h-full rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex flex-col overflow-y-auto overflow-x-hidden ${isCollection ? '' : 'border'} ${isSemanticAlternative ? 'bg-gray-50 border-gray-300' : 'bg-white'} ${isCatalogRental ? 'ring-2 ring-green-500/70' : ''}`}
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            MozBackfaceVisibility: 'hidden',
            transform: 'rotateY(0deg)',
            zIndex: isFlipped ? 1 : 2,
            padding: `${scaledPadding}px`,
            scrollbarWidth: 'thin',
            scrollbarColor: '#cfd2d6 transparent',
            ...collectionGlowStyle  // Apply collection glow if this is a variant
          }}
          title={isSemanticAlternative ? 'Pure Semantic Search result - found via AI-powered meaning search, not exact filter match' : undefined}
        >
      <div className="flex justify-between items-start" style={{ marginBottom: `${scaledMargin}px` }}>
        {/* min-w-0 + break-words: a flex item defaults to min-width:auto, so it
            refuses to shrink below its content's intrinsic width. Product names
            carry long unbreakable tokens ("LSPOLO-100%POLY PERFORMANCE"), which
            pushed this row wider than the card and painted over the neighbouring
            card once the grid track got narrow. */}
        <h3 className="font-semibold text-gray-900 mr-2 min-w-0 break-words" style={{ fontSize: `${scaledTitleSize}px`, lineHeight: '1.3' }}>{product.name}</h3>
            {product.price && <div className="font-bold text-teal-600 shrink-0" style={{ fontSize: `${scaledSmallText}px` }}>SC: {product.price}</div>}
      </div>
          
          <div className="text-gray-400" style={{ fontSize: `${scaledTinyText}px`, marginBottom: `${Math.round(scaledMargin * 0.5)}px` }}>Click to flip for details</div>

      <div className="flex justify-between items-center" style={{ marginBottom: `${scaledMargin}px` }}>
        <div className="text-gray-500 min-w-0 truncate" style={{ fontSize: `${scaledTinyText}px` }}>Style: {product.attributes?.style_code || sku?.substring(0, 4) || sku}</div>
        {(selected?.vendor_sku || (product.attributes as any)?.vendor_sku) && (
          <div className="text-gray-400 shrink-0 ml-2 truncate" style={{ fontSize: `${scaledTinyText}px`, maxWidth: '60%' }}>Vendor SKU: {selected?.vendor_sku || (product.attributes as any).vendor_sku}</div>
        )}
      </div>

          {/* Curated Related Badge - purple badge in bottom-left corner */}
          {isCuratedRelated && (
            <div 
              className="absolute bottom-2 left-2"
              style={{ zIndex: 10 }}
              title="Curated related product from catalog"
            >
              <div 
                className="flex items-center gap-1 bg-purple-100 text-purple-700 rounded-full shadow-sm px-1.5 py-0.5 border border-purple-200"
                style={{ fontSize: `${Math.max(8, Math.round(10 * scale))}px` }}
              >
                <svg 
                  className="w-2.5 h-2.5"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">Curated</span>
              </div>
            </div>
          )}
          
          {/* Semantic Search Indicator - subtle badge in bottom-right corner */}
          {isSemanticAlternative && (
            <div 
              className="absolute bottom-2 right-2"
              style={{ zIndex: 10 }}
              title="Pure Semantic Search result"
            >
              <div 
                className="flex items-center gap-1 bg-gray-200 text-gray-600 rounded-full shadow-sm px-1.5 py-0.5"
                style={{ fontSize: `${Math.max(8, Math.round(10 * scale))}px` }}
              >
                <svg 
                  className="w-2.5 h-2.5"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="font-medium">AI</span>
              </div>
            </div>
          )}
          
          {/* Collection Pip - small colored dot in top-right corner */}
          {isCollection && (
            <div 
              className="absolute top-2 right-2"
              style={{ zIndex: 10 }}
              title="Part of a product collection"
            >
              <div 
                className="rounded-full shadow-md"
                style={{ 
                  width: `${Math.max(10, Math.round(14 * scale))}px`,
                  height: `${Math.max(10, Math.round(14 * scale))}px`,
                  backgroundColor: collectionGlowColor,
                  border: '2px solid white'
                }}
              />
            </div>
          )}
          
          {/* Criteria Badges */}
          <div className="flex flex-wrap" style={{ gap: `${Math.max(4, Math.round(6 * scale))}px`, marginBottom: `${scaledMargin}px` }}>
            {badges.map((badge: string, idx: number) => {
              // Apply special styling for different badge types
              const isMadeByUniFirst = badge === 'Made by UniFirst';
              const isBestseller = badge.includes('Bestseller') || badge.includes('⭐');
              const isIndustryTop10 = badge.includes('Top 10');
              const isIndustryMatch = badge.startsWith('🏭');
              return (
                <span 
                  key={idx}
                  className={`inline-flex items-center rounded-full font-medium ${
                    isIndustryTop10
                      ? 'bg-gradient-to-r from-amber-100 via-yellow-200 to-amber-100 text-amber-900 border border-amber-400 shadow-sm'
                      : isIndustryMatch
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                      : isBestseller
                      ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-900 border border-yellow-300 shadow-sm'
                      : isMadeByUniFirst 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-teal-100 text-teal-800'
                  }`}
                  style={{ 
                    fontSize: `${scaledTinyText}px`, 
                    padding: `${Math.max(1, Math.round(2 * scale))}px ${Math.max(4, Math.round(8 * scale))}px`,
                    ...(isIndustryTop10 ? { fontWeight: 600 } : {})
                  }}
                >
                  {badge}
                </span>
              );
            })}
            {stockItems > 0 && (
              <span 
                className="inline-flex items-center rounded-full font-medium bg-green-100 text-green-800"
                style={{ 
                  fontSize: `${scaledTinyText}px`, 
                  padding: `${Math.max(1, Math.round(2 * scale))}px ${Math.max(4, Math.round(8 * scale))}px` 
                }}
              >
                {stockItems} in stock
              </span>
            )}
            {canadaOnly && (
              <span 
                className="inline-flex items-center rounded-full font-medium bg-red-100 text-red-800"
                style={{ 
                  fontSize: `${scaledTinyText}px`, 
                  padding: `${Math.max(1, Math.round(2 * scale))}px ${Math.max(4, Math.round(8 * scale))}px` 
                }}
              >
                CAN Only
              </span>
            )}
            {directSaleOnly && (
              <span 
                className="inline-flex items-center rounded-full font-medium bg-purple-100 text-purple-800"
                style={{ 
                  fontSize: `${scaledTinyText}px`, 
                  padding: `${Math.max(1, Math.round(2 * scale))}px ${Math.max(4, Math.round(8 * scale))}px` 
                }}
              >
                Direct Sale Only
              </span>
            )}
            {rentalAndDirectSale && (
              <span 
                className="inline-flex items-center rounded-full font-medium bg-brand-100 text-brand-800"
                style={{ 
                  fontSize: `${scaledTinyText}px`, 
                  padding: `${Math.max(1, Math.round(2 * scale))}px ${Math.max(4, Math.round(8 * scale))}px` 
                }}
              >
                Rental &amp; Direct Sale
              </span>
            )}
            {salesChannelUnknown && (
              <span 
                className="inline-flex items-center rounded-full font-medium bg-gray-100 text-gray-500"
                style={{ 
                  fontSize: `${scaledTinyText}px`, 
                  padding: `${Math.max(1, Math.round(2 * scale))}px ${Math.max(4, Math.round(8 * scale))}px` 
                }}
              >
                Sales Channel Unknown
              </span>
            )}
          </div>

          <div 
            className="w-full flex justify-center relative overflow-hidden"
            style={{ minHeight: `${scaledImageHeight}px`, marginBottom: `${scaledMargin}px` }}
          >
            {/* Show fallback only while loading or on error */}
        {(!imageLoaded || imageError) && (
          <div className="w-full h-full flex items-center justify-center bg-white dark:bg-neutral-800 rounded-md">
            <img 
              src={fallbackImageUrl} 
              alt={`${product.name} (fallback)`} 
                  className="max-h-48 max-w-48 opacity-40 object-contain"
            />
          </div>
        )}
        
        {/* Show loading overlay */}
        {imageIsLoading && !imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md">
            <div className="animate-pulse text-gray-500 bg-white px-3 py-1 rounded-full shadow-sm">
              Loading image...
            </div>
          </div>
        )}
        
        {/* Show the real image on top when loaded */}
        {imageUrl && !imageError && imageLoaded && (
          <>
            <img 
              src={imageUrl} 
              alt={product.name} 
              className="absolute inset-0 w-full h-full max-h-80 object-contain rounded-md transition-opacity duration-300 opacity-100"
              loading="lazy"
              style={{ 
                zIndex: 10,
                maxWidth: '100%',
                maxHeight: '100%'
              }}
            />
            <button
              onClick={(e) => { e.stopPropagation(); setIsImageModalOpen(true); }}
              className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-white/80 hover:bg-white text-gray-500 hover:text-gray-800 shadow-sm border border-gray-200/60 transition-all opacity-0 group-hover:opacity-100 hover:scale-110"
              style={{ zIndex: 15 }}
              title="View full size"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
              </svg>
            </button>
          </>
        )}
      </div>

      {product.color_variants && product.color_variants.length > 0 && (
            <div className="flex flex-wrap" style={{ gap: `${Math.max(4, Math.round(6 * scale))}px`, marginBottom: `${scaledMargin}px` }}>
          {product.color_variants.map((v: ColorVariant) => {
            const sanitizedSwatchUrl = sanitizeUrl(v.swatch_url || undefined);
            const swatchUrl = getProxiedImageUrl(sanitizedSwatchUrl);
            // IMPORTANT: Check the proxied URL (what's actually rendered and preloaded)
            const swatchLoaded = swatchUrl ? isImageLoaded(swatchUrl) : false;
            const swatchError = swatchUrl ? getImageError(swatchUrl) : false;
            const swatchSize = Math.max(16, Math.round(24 * scale));
            
            return (
              <button
                key={v.sku}
                title={v.color_name && v.color_code ? `${v.color_name} (${v.color_code})` : v.color_name || v.color_code || v.sku}
                className={`rounded-full border transition-all duration-150 ${
                  selected?.sku === v.sku 
                    ? 'border-teal-500 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ padding: `${Math.max(1, Math.round(2 * scale))}px` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVariantSelect(v);
                    }}
              >
                <div 
                      className="rounded-full overflow-hidden" 
                  style={{ 
                    // color_code is an internal code (e.g., "Swatch-01.jpg" or "01"), not a CSS color.
                    backgroundColor: '#e3e5e8',
                    width: `${swatchSize}px`,
                    height: `${swatchSize}px`
                  }}
                >
                  {swatchUrl && !swatchError && swatchLoaded ? (
                    <img 
                      src={swatchUrl} 
                      alt={v.color_name || v.color_code || ''} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div 
                      className="w-full h-full" 
                      style={{ backgroundColor: '#e3e5e8' }} 
                      title={`${v.color_name || v.color_code || 'Color'} swatch`}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

          {/* Selected color label */}
          {selected && (selected.color_name || selected.color_code) && (
            <div className="text-gray-500" style={{ fontSize: `${scaledTinyText}px`, marginBottom: `${Math.round(scaledMargin * 0.5)}px` }}>
              {selected.color_name && selected.color_code 
                ? `${selected.color_name} (${selected.color_code})`
                : selected.color_name || selected.color_code}
            </div>
          )}

          {/* Size Options */}
          {product.attributes?.sizes && product.attributes.sizes !== 'Not specified' && (
            <div style={{ marginBottom: `${scaledMargin}px` }}>
              <div className="text-gray-500 font-medium" style={{ fontSize: `${scaledTinyText}px`, marginBottom: `${Math.round(scaledMargin * 0.5)}px` }}>
                Sizes
              </div>
              <div className="flex flex-wrap" style={{ gap: `${Math.max(3, Math.round(4 * scale))}px` }}>
                {(() => {
                  const sizesStr = product.attributes.sizes;
                  // Parse size ranges like "XS–4XL" or "S–3XL" into individual sizes
                  const allSizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
                  const rangeMatch = sizesStr.match(/^([A-Z0-9]+)[–-]([A-Z0-9]+)$/i);
                  
                  let displaySizes: string[] = [];
                  if (rangeMatch) {
                    const startSize = rangeMatch[1].toUpperCase();
                    const endSize = rangeMatch[2].toUpperCase();
                    const startIdx = allSizes.findIndex(s => s === startSize);
                    const endIdx = allSizes.findIndex(s => s === endSize);
                    if (startIdx !== -1 && endIdx !== -1 && startIdx <= endIdx) {
                      displaySizes = allSizes.slice(startIdx, endIdx + 1);
                    } else {
                      // Fallback: just show the original string
                      displaySizes = [sizesStr];
                    }
                  } else if (sizesStr.includes(',')) {
                    // Comma-separated sizes
                    displaySizes = sizesStr.split(',').map((s: string) => s.trim()).filter(Boolean);
                  } else {
                    // Single size or unrecognized format
                    displaySizes = [sizesStr];
                  }
                  
                  return displaySizes.map((size: string, idx: number) => (
                    <span
                      key={idx}
                      className="inline-flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-gray-700 font-medium"
                      style={{
                        fontSize: `${Math.max(8, Math.round(10 * scale))}px`,
                        padding: `${Math.max(1, Math.round(2 * scale))}px ${Math.max(4, Math.round(6 * scale))}px`,
                        minWidth: `${Math.max(20, Math.round(28 * scale))}px`
                      }}
                    >
                      {size}
                    </span>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Why This Product - Reasons (capped at 4) */}
          {reasons.length > 0 && (
            <div className="flex-shrink-0 border-t border-gray-100" style={{ marginTop: `${scaledMargin}px`, paddingTop: `${scaledMargin}px` }}>
              {/* Mobile/Tablet: Collapsible */}
              <div className="lg:hidden">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsReasonsExpanded(!isReasonsExpanded);
                  }}
                  className="w-full flex items-center justify-between font-semibold text-gray-700 hover:text-teal-600 transition-colors"
                  style={{ fontSize: `${scaledTinyText}px`, marginBottom: `${Math.round(scaledMargin * 0.5)}px` }}
                >
                  <span>Differentiating Features:</span>
                  <svg
                    className={`transition-transform ${isReasonsExpanded ? 'rotate-180' : ''}`}
                    style={{ width: `${Math.max(12, Math.round(16 * scale))}px`, height: `${Math.max(12, Math.round(16 * scale))}px` }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isReasonsExpanded && (
                  <ul className="list-disc list-inside" style={{ marginTop: `${Math.round(scaledMargin * 0.5)}px` }}>
                    {reasons.slice(0, scale < 0.6 ? 2 : 4).map((reason: string, idx: number) => (
                      <li key={idx} className="text-gray-700" style={{ fontSize: `${scaledTinyText}px`, lineHeight: '1.4', marginBottom: `${Math.round(scaledMargin * 0.25)}px` }}>
                        {reason}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
              {/* Desktop: Always Expanded */}
              <div className="hidden lg:block">
                <div className="font-semibold text-gray-700" style={{ fontSize: `${scaledTinyText}px`, marginBottom: `${Math.round(scaledMargin * 0.5)}px` }}>Differentiating Features:</div>
                <ul className="list-disc list-inside">
                  {reasons.slice(0, scale < 0.6 ? 2 : 4).map((reason: string, idx: number) => (
                    <li key={idx} className="text-gray-700" style={{ fontSize: `${scaledTinyText}px`, lineHeight: '1.4', marginBottom: `${Math.round(scaledMargin * 0.25)}px` }}>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

        </div>

        {/* BACK SIDE - Scrollable with all details */}
        <div 
          className={`absolute w-full h-full rounded-xl bg-gradient-to-br from-teal-50 to-white shadow-sm hover:shadow-md transition-all duration-200 overflow-y-auto overflow-x-hidden ${isCollection ? '' : 'border'}`}
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            MozBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            zIndex: isFlipped ? 2 : 1,
            padding: `${scaledPadding}px`,
            ...collectionGlowStyle  // Apply collection glow if this is a variant
          }}
        >
          <div className="flex justify-between items-start sticky top-0 bg-gradient-to-br from-teal-50 to-white" style={{ marginBottom: `${scaledMargin}px`, paddingBottom: `${scaledMargin}px` }}>
            <h3 className="font-semibold text-gray-900 min-w-0 break-words" style={{ fontSize: `${scaledTitleSize}px`, lineHeight: '1.3' }}>{product.name}</h3>
            <div className="text-teal-600 italic shrink-0 ml-2" style={{ fontSize: `${scaledTinyText}px` }}>
              Click anywhere to flip back
            </div>
          </div>

          <div className="flex justify-between items-center" style={{ marginBottom: `${scaledMargin * 1.5}px` }}>
            <div className="text-gray-500" style={{ fontSize: `${scaledTinyText}px` }}>Style: {product.attributes?.style_code || sku?.substring(0, 4) || sku}</div>
            {(selected?.vendor_sku || (product.attributes as any)?.vendor_sku) && (
              <div className="text-gray-400" style={{ fontSize: `${scaledTinyText}px` }}>Vendor SKU: {selected?.vendor_sku || (product.attributes as any).vendor_sku}</div>
            )}
          </div>

          {/* Comprehensive Product Details - ALL INDEX FIELDS */}
          <div className="space-y-2 text-sm pb-4">
            {/* === DESCRIPTIONS SECTION === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Description</div>
              <div className="text-gray-600 text-xs leading-relaxed">
                {(product.attributes as any)?.long_description || 
                 (product.attributes as any)?.Mozart_Desc || 
                 (product.attributes as any)?.mozart_description || 
                 product.description || 
                 <span className="text-gray-400 italic">Not specified</span>}
              </div>
            </div>

            {/* === PRICING & AVAILABILITY === */}
            <div className="grid grid-cols-2 gap-2 mb-3 pb-2 border-b border-gray-200">
              <div>
                <div className="font-semibold text-gray-700 text-xs">Standard Cost:</div>
                <div className={product.price ? "text-teal-600 font-bold" : "text-gray-400 italic text-xs"}>
                  {product.price || 'Not specified'}
                </div>
              </div>
              <div>
                <div className="font-semibold text-gray-700 text-xs">Stock Items:</div>
                <div className={stockItems > 0 ? "text-green-600 font-medium" : "text-gray-400 italic text-xs"}>
                  {stockItems > 0 ? `${stockItems} units` : 'Not specified'}
                </div>
              </div>
            </div>

            {/* === IDENTITY / SKUs === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Product Identity</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-600">Style Code:</span>{' '}
                  <span className={product.attributes?.style_code ? "text-gray-800" : "text-gray-400 italic"}>
                    {product.attributes?.style_code || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Partial SKU:</span>{' '}
                  <span className={selected?.sku || (product as any).partialSku_OPIM || product.base_sku ? "text-gray-800" : "text-gray-400 italic"}>
                    {selected?.sku || (product as any).partialSku_OPIM || product.base_sku || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Catalog SKU:</span>{' '}
                  <span className={(product.attributes as any)?.catalog_sku ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.catalog_sku || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Original SKU:</span>{' '}
                  <span className={(product.attributes as any)?.original_sku ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.original_sku || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Matched SKU:</span>{' '}
                  <span className={(product.attributes as any)?.matched_sku ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.matched_sku || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* === TAXONOMY / CATEGORIES === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Categories</div>
              <div className="grid grid-cols-1 gap-1 text-xs">
                <div>
                  <span className="font-medium text-gray-600">Class:</span>{' '}
                  <span className={(product.attributes as any)?.class_description ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.class_description || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Sub-Class:</span>{' '}
                  <span className={(product.attributes as any)?.sub_class_description ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.sub_class_description || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Product Type:</span>{' '}
                  <span className={(product.attributes as any)?.product_sub_type_category || product.attributes?.category ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.product_sub_type_category || product.attributes?.category || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Industry:</span>{' '}
                  <span className={(product.attributes as any)?.industry ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.industry || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Use Case:</span>{' '}
                  <span className={(product.attributes as any)?.spec_app_description ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.spec_app_description || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>

            {/* === ATTRIBUTES === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Attributes</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-600">Gender:</span>{' '}
                  <span className={(product.attributes as any)?.gender ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.gender || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Sizes:</span>{' '}
                  <span className={(product.attributes as any)?.sizes ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.sizes || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Color Keywords:</span>{' '}
                  <span className={(product.attributes as any)?.color_keywords ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.color_keywords || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Variety:</span>{' '}
                  <span className={(product.attributes as any)?.variety ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.variety || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Line:</span>{' '}
                  <span className={(product.attributes as any)?.line ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.line || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>

            {/* === FABRIC & MATERIALS === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Fabric & Materials</div>
              <div className="space-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-600">Fabric Description:</span>{' '}
                  <span className={(product.attributes as any)?.fabric_description ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.fabric_description || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Fabrics:</span>{' '}
                  <span className={(product.attributes as any)?.fabrics ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.fabrics || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>

            {/* === SAFETY & COMPLIANCE === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Safety & Compliance</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-600">Fire Resistant:</span>{' '}
                  <span className={(product.attributes as any)?.fire_resistant !== undefined ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.fire_resistant === true ? 'Yes' : 
                     (product.attributes as any)?.fire_resistant === false ? 'No' : 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Hi-Visibility:</span>{' '}
                  <span className={(product.attributes as any)?.hi_visibility ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.hi_visibility || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Enhanced Visibility:</span>{' '}
                  <span className={(product.attributes as any)?.enhanced_vis !== undefined || (product.attributes as any)?.enhanced_visibility !== undefined ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.enhanced_vis === true || (product.attributes as any)?.enhanced_visibility === true ? 'Yes' : 
                     (product.attributes as any)?.enhanced_vis === false || (product.attributes as any)?.enhanced_visibility === false ? 'No' : 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">ANSI Class:</span>{' '}
                  <span className={(product.attributes as any)?.ansi_class ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.ansi_class || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">CAT Rating:</span>{' '}
                  <span className={(product.attributes as any)?.cat_rating ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.cat_rating || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">ATPV:</span>{' '}
                  <span className={(product.attributes as any)?.atpv ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.atpv ? `${(product.attributes as any).atpv} cal/cm²` : 'Not specified'}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-600">Certifications:</span>{' '}
                  <span className={(product.attributes as any)?.certifications ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.certifications || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>

            {/* === BRAND & VENDOR === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Brand & Vendor</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-600">Brand:</span>{' '}
                  <span className={(product.attributes as any)?.parent_brand ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.parent_brand || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Vendor:</span>{' '}
                  <span className="text-gray-800">
                    {madeByUniFirst ? 'UniFirst' : ((product.attributes as any)?.vendorname || (product.attributes as any)?.vendor_name || 'Not specified')}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Vendor SKU:</span>{' '}
                  <span className={selected?.vendor_sku || (product.attributes as any)?.vendor_sku ? "text-gray-800" : "text-gray-400 italic"}>
                    {selected?.vendor_sku || (product.attributes as any)?.vendor_sku || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Made by UniFirst:</span>{' '}
                  <span className={madeByUniFirst ? "text-blue-600 font-medium" : "text-gray-400 italic"}>
                    {madeByUniFirst ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* === SALES & AVAILABILITY === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Sales & Availability</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <span className="font-medium text-gray-600">Sales Channel:</span>{' '}
                  <span className={(product.attributes as any)?.Direct_Sale_Only_or_Both ? "text-gray-800" : "text-gray-400 italic"}>
                    {(product.attributes as any)?.Direct_Sale_Only_or_Both || 'Not specified'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Canada Only:</span>{' '}
                  <span className={canadaOnly ? "text-red-600 font-medium" : "text-gray-400 italic"}>
                    {canadaOnly ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* === TAGS & ATTRIBUTES === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Tags & Attributes</div>
              <div className="text-xs">
                <span className="font-medium text-gray-600">Tags:</span>{' '}
                <span className={(product.attributes as any)?.tags_attributes ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.tags_attributes || 'Not specified'}
                </span>
              </div>
            </div>

            {/* === RELATED PRODUCTS === */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Related Products</div>
              <div className="text-xs">
                <span className={(product.attributes as any)?.related_products ? "text-gray-800" : "text-gray-400 italic"}>
                  {(product.attributes as any)?.related_products || 'Not specified'}
                </span>
              </div>
            </div>

            {/* === STYLE DESCRIPTION === */}
            {((product.attributes as any)?.style_desc || (product.attributes as any)?.style_description) && (
              <div className="mb-3 pb-2 border-b border-gray-200">
                <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Style Description</div>
                <div className="text-gray-600 text-xs leading-relaxed">
                  {(product.attributes as any)?.style_desc || (product.attributes as any)?.style_description}
                </div>
              </div>
            )}

            {/* === COLOR VARIANTS === */}
            <div className="mb-2">
              <div className="font-semibold text-gray-700 mb-1 text-xs uppercase tracking-wide">Available Colors</div>
              <div className="text-xs">
                <span className={product.color_variants && product.color_variants.length > 0 ? "text-gray-800" : "text-gray-400 italic"}>
                  {product.color_variants && product.color_variants.length > 0 
                    ? product.color_variants.map((v: ColorVariant) => v.color_name || v.color_code).filter(Boolean).join(', ') || `${product.color_variants.length} variant(s)`
                    : 'Single color only'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-size image modal — rendered via portal so it escapes card transforms */}
      {isImageModalOpen && imageUrl && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div
            className="relative max-w-[90vw] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imageUrl}
              alt={product.name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg px-4 py-3">
              <div className="text-white font-semibold text-sm">{product.name}</div>
              {selected?.color_name && (
                <div className="text-white/80 text-xs">{selected.color_name}</div>
              )}
            </div>
            <button
              onClick={() => setIsImageModalOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Memoize to avoid re-renders while streaming text updates
const ProductCard = React.memo(ProductCardComponent);

export default ProductCard;
